import type { ChangePasswordFailure } from '@wehunt/ui'

/**
 * Which failure the Settings page should report for a rejected password change.
 *
 * Passwords are owned by Firebase Auth, not the clubhouse API, so Firebase's
 * error codes are what reach the user. Each of these deserves its own message:
 * a single generic failure would leave someone retyping a correct new password
 * because we never told them it was the OLD one that was wrong.
 *
 * Anything unrecognised falls back to `unknown` rather than surfacing a raw
 * `auth/...` code, which would leak English into a translated UI and mean
 * nothing to a hunter.
 */
const REASON_BY_CODE: Readonly<Record<string, ChangePasswordFailure>> = {
  'auth/wrong-password': 'wrong-password',
  // What a modern Firebase SDK returns instead of wrong-password once email
  // enumeration protection is on; the older shape is still emitted by some
  // versions, so both map to the same answer.
  'auth/invalid-credential': 'wrong-password',
  'auth/invalid-login-credentials': 'wrong-password',
  'auth/weak-password': 'weak-password',
  'auth/requires-recent-login': 'requires-recent-login',
  'auth/user-token-expired': 'requires-recent-login',
  'auth/too-many-requests': 'too-many-requests',
}

export function changePasswordFailure(error: unknown): ChangePasswordFailure {
  const code = (error as { code?: unknown } | null | undefined)?.code
  return (typeof code === 'string' ? REASON_BY_CODE[code] : undefined) ?? 'unknown'
}
